import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckSquare,
  Square,
  Edit2,
  ExternalLink,
  CheckCircle2,
  Save,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Flag,
  Plus,
  Trash2,
  Shield,
  AlertTriangle,
  Info,
} from "lucide-react";
import { DiffViewer } from "../DiffViewer";
import type { RuleResponse } from "../../gen/models";
import { normalizeAgentType } from "../../hooks/useUserDirectory";

interface RuleChangesetCardProps {
  rule: any;
  baseline: RuleResponse | null | undefined;
  isExcluded: boolean;
  isCompleted: boolean;
  onToggleExclude: (id: string) => void;
  onSave?: (ruleId: string, updates: { title: string; instruction: string; priority: number }) => Promise<void>;
  onOpenEditLegacy?: (rule: any) => void;
}

function RuleChangesetCard({
  rule,
  baseline,
  isExcluded,
  isCompleted,
  onToggleExclude,
  onSave,
  onOpenEditLegacy,
}: RuleChangesetCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(rule.title || "");
  const [editInstruction, setEditInstruction] = useState(rule.instruction || "");
  const [editPriority, setEditPriority] = useState<number>(rule.priority ?? 100);
  const [isSaving, setIsSaving] = useState(false);

  const handleStartEdit = () => {
    if (!onSave && onOpenEditLegacy) {
      onOpenEditLegacy(rule);
      return;
    }
    setEditTitle(rule.title || "");
    setEditInstruction(rule.instruction || "");
    setEditPriority(rule.priority ?? 100);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditTitle(rule.title || "");
    setEditInstruction(rule.instruction || "");
    setEditPriority(rule.priority ?? 100);
    setIsEditing(false);
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!onSave) return;
    setIsSaving(true);
    try {
      await onSave(rule.id, {
        title: editTitle.trim() || rule.title,
        instruction: editInstruction.trim() || rule.instruction,
        priority: editPriority,
      });
      setIsEditing(false);
    } catch {
      // Error handled by parent
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={`p-4 rounded-xl border transition ${
        isEditing
          ? "bg-white dark:bg-stone-900 border-emerald-500/60 dark:border-emerald-500/50 shadow-md ring-2 ring-emerald-500/10 space-y-4"
          : isExcluded
          ? "opacity-55 grayscale-[35%] bg-stone-50/80 dark:bg-stone-950/80 border-dashed border-stone-300 dark:border-stone-800 space-y-3"
          : "bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-800 shadow-2xs space-y-3"
      }`}
    >
      {/* Card Header */}
      <div className="flex items-start justify-between gap-4">
        {/* Left Side: Rule Info or In-Place Edit Form */}
        <div className="min-w-0 flex-1">
          {isEditing ? (
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-semibold text-stone-700 dark:text-stone-300">
                    Rule Title
                  </label>
                  {baseline && baseline.title !== editTitle && (
                    <span className="text-[10px] text-stone-400 font-mono">
                      Baseline: {baseline.title}
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full text-xs font-bold rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50/60 dark:bg-stone-950/60 px-2.5 py-1.5 text-stone-900 dark:text-stone-100 focus:bg-white dark:focus:bg-stone-900 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  placeholder="Rule title..."
                />
              </div>

              {/* Priority & Metadata Controls */}
              <div className="flex items-center flex-wrap gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <label className="text-[11px] font-semibold text-stone-700 dark:text-stone-300">
                    Priority:
                  </label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={editPriority}
                      onChange={(e) => setEditPriority(parseInt(e.target.value) || 0)}
                      className="w-20 text-xs font-mono font-semibold rounded-md border border-stone-200 dark:border-stone-700 bg-stone-50/60 dark:bg-stone-950/60 px-2 py-1 text-stone-900 dark:text-stone-100 focus:bg-white dark:focus:bg-stone-900 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                    <button
                      type="button"
                      onClick={() => setEditPriority((p) => Math.max(0, p - 10))}
                      className="px-1.5 py-1 rounded border border-stone-200 dark:border-stone-700 bg-stone-100 dark:bg-stone-800 text-[10px] font-mono text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700 transition"
                      title="Decrease priority by 10"
                    >
                      -10
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditPriority((p) => p + 10)}
                      className="px-1.5 py-1 rounded border border-stone-200 dark:border-stone-700 bg-stone-100 dark:bg-stone-800 text-[10px] font-mono text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700 transition"
                      title="Increase priority by 10"
                    >
                      +10
                    </button>
                  </div>
                  {baseline && baseline.priority !== editPriority && (
                    <span className="text-[11px] font-mono text-amber-600 dark:text-amber-400">
                      (baseline was {baseline.priority})
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 text-[11px] text-stone-500 font-mono">
                  <span>
                    Category: <strong className="text-stone-700 dark:text-stone-300 font-semibold">{rule.category}</strong>
                  </span>
                  <span>•</span>
                  <span>
                    Agent: <strong className="text-stone-700 dark:text-stone-300 font-semibold">{rule.agent_type ? normalizeAgentType(rule.agent_type) : "All"}</strong>
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center flex-wrap gap-2">
                {isCompleted ? (
                  <Link
                    to={`/rules/${rule.id}`}
                    className="group inline-flex items-center gap-1 text-xs font-bold text-stone-900 dark:text-stone-100 hover:text-emerald-600 dark:hover:text-emerald-400 transition"
                  >
                    <span className="group-hover:underline truncate">{rule.title}</span>
                    <ExternalLink className="w-3 h-3 text-stone-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 shrink-0" />
                  </Link>
                ) : (
                  <div className="flex items-center flex-wrap gap-1.5">
                    {baseline && baseline.title !== rule.title ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs text-stone-400 dark:text-stone-500 line-through">
                          {baseline.title}
                        </span>
                        <span className="text-stone-400 text-xs">→</span>
                        <h4 className="text-xs font-bold text-stone-900 dark:text-stone-100">
                          {rule.title}
                        </h4>
                      </div>
                    ) : (
                      <h4 className="text-xs font-bold text-stone-900 dark:text-stone-100">
                        {rule.title}
                      </h4>
                    )}
                  </div>
                )}

                {baseline && baseline.category !== rule.category ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono">
                    <span className="px-1.5 py-0.2 rounded bg-stone-100 dark:bg-stone-800 uppercase text-stone-400 line-through">
                      {baseline.category}
                    </span>
                    <span className="text-stone-400">→</span>
                    <span className="px-1.5 py-0.2 rounded bg-emerald-50 dark:bg-emerald-950 uppercase text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 font-bold">
                      {rule.category}
                    </span>
                  </span>
                ) : (
                  <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-stone-100 dark:bg-stone-800 uppercase text-stone-600 dark:text-stone-300">
                    {rule.category}
                  </span>
                )}

                {isCompleted && (
                  <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                    Active Rule
                  </span>
                )}

                {rule.supersedes_rule_id ? (
                  <Link
                    to={`/rules/${rule.supersedes_rule_id}`}
                    className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.2 rounded bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900 transition"
                    title="View superseded rule in catalog"
                  >
                    <span>Revision (Supersedes prior)</span>
                    <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                  </Link>
                ) : (
                  <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                    New Rule
                  </span>
                )}

                {((isEditing ? editInstruction : rule.instruction) || "").length > 1000 && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.2 rounded bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-800 shadow-2xs"
                    title="Long directive (>1,000 chars) increases prompt token consumption, adds turn latency, and may dilute model attention across operational constraints."
                  >
                    <AlertTriangle className="w-2.5 h-2.5 text-amber-600 dark:text-amber-400 shrink-0" />
                    <span>{((isEditing ? editInstruction : rule.instruction) || "").length.toLocaleString()} chars (&gt;1,000)</span>
                  </span>
                )}

                {rule.is_stale && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.2 rounded bg-rose-50 dark:bg-rose-950 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 ring-1 ring-rose-500/20"
                    title={rule.stale_reason || "Outdated baseline: this rule has already been altered by another version"}
                  >
                    <AlertTriangle className="w-2.5 h-2.5 text-rose-500 shrink-0" />
                    <span>Outdated Version</span>
                  </span>
                )}
              </div>

              {/* Priority and Agent row */}
              <div className="flex items-center flex-wrap gap-2 text-[11px] font-mono mt-1">
                {baseline && baseline.priority !== rule.priority ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-950/60 text-amber-800 dark:text-amber-200 border border-amber-200/80 dark:border-amber-800/80">
                    <span className="font-semibold">Priority:</span>
                    <span className="line-through text-stone-400 dark:text-stone-500 font-normal">
                      {baseline.priority}
                    </span>
                    <span className="text-amber-600 dark:text-amber-400">→</span>
                    <span className="font-bold text-amber-950 dark:text-amber-100">
                      {rule.priority}
                    </span>
                  </span>
                ) : (
                  <span className="text-stone-400 dark:text-stone-500">
                    Priority: <span className="text-stone-600 dark:text-stone-300 font-medium">{rule.priority}</span>
                  </span>
                )}

                <span className="text-stone-300 dark:text-stone-700">•</span>

                {baseline && baseline.agent_type !== rule.agent_type ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-950/60 text-amber-800 dark:text-amber-200 border border-amber-200/80 dark:border-amber-800/80">
                    <span className="font-semibold">Agent:</span>
                    <span className="line-through text-stone-400 dark:text-stone-500 font-normal">
                      {baseline.agent_type ? normalizeAgentType(baseline.agent_type) : "All"}
                    </span>
                    <span className="text-amber-600 dark:text-amber-400">→</span>
                    <span className="font-bold text-amber-950 dark:text-amber-100">
                      {rule.agent_type ? normalizeAgentType(rule.agent_type) : "All"}
                    </span>
                  </span>
                ) : (
                  <span className="text-stone-400 dark:text-stone-500">
                    Agent: <span className="text-stone-600 dark:text-stone-300 font-medium">{rule.agent_type ? normalizeAgentType(rule.agent_type) : "All"}</span>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Side: Uncheck/Include toggle button moved to top right + In-place edit/save buttons */}
        <div className="flex items-center gap-2 shrink-0">
          {!isCompleted && (
            <button
              type="button"
              onClick={() => onToggleExclude(rule.id)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition shadow-2xs ${
                isExcluded
                  ? "border border-stone-200 dark:border-stone-700 bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700 hover:text-stone-700 dark:hover:text-stone-200"
                  : "border border-emerald-200 dark:border-emerald-800/80 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/60"
              }`}
              title={isExcluded ? "Click to include in batch" : "Click to exclude from batch"}
            >
              {isExcluded ? (
                <>
                  <Square className="w-3.5 h-3.5 text-stone-400" />
                  <span>Excluded</span>
                </>
              ) : (
                <>
                  <CheckSquare className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                  <span>Included</span>
                </>
              )}
            </button>
          )}

          {isCompleted ? (
            <Link
              to={`/rules/${rule.id}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 text-xs font-medium hover:bg-stone-50 dark:hover:bg-stone-700 hover:text-emerald-600 dark:hover:text-emerald-400 transition shadow-2xs shrink-0"
            >
              <span>Open Rule</span>
              <ExternalLink className="w-3 h-3" />
            </Link>
          ) : isEditing ? (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={isSaving}
                className="px-2.5 py-1 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 text-xs hover:bg-stone-100 dark:hover:bg-stone-800 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleSave()}
                disabled={isSaving}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-2xs disabled:opacity-50 transition"
              >
                {isSaving ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <Save className="w-3 h-3" />
                )}
                <span>Save</span>
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleStartEdit}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 text-xs font-medium hover:bg-stone-50 dark:hover:bg-stone-700 hover:text-stone-900 dark:hover:text-stone-100 transition shadow-2xs shrink-0"
            >
              <Edit2 className="w-3 h-3" />
              <span>Edit</span>
            </button>
          )}
        </div>
      </div>

      {/* Exclusion notice banner when unchecked */}
      {isExcluded && !isEditing && (
        <div className="px-3 py-1.5 rounded-lg bg-stone-100 dark:bg-stone-800/80 border border-stone-200 dark:border-stone-700/60 text-[11px] text-stone-500 dark:text-stone-400 flex items-center justify-between">
          <span className="font-medium">
            Rule unchecked — will be excluded when promoting batch
          </span>
          <button
            type="button"
            onClick={() => onToggleExclude(rule.id)}
            className="text-emerald-600 dark:text-emerald-400 hover:underline font-semibold text-[11px]"
          >
            Re-include
          </button>
        </div>
      )}

      {/* Outdated warning notice */}
      {rule.is_stale && !isCompleted && (
        <div className="px-3 py-2 rounded-lg bg-rose-50/90 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-800/80 text-[11px] text-rose-800 dark:text-rose-200 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-rose-600 dark:text-rose-400" />
          <div className="space-y-0.5 min-w-0">
            <span className="font-semibold">Conflict / Outdated Baseline:</span>{" "}
            <span>{rule.stale_reason || "The base rule targeted by this proposed update has already been superseded or altered."}</span>
          </div>
        </div>
      )}

      {/* In-place Editing Area or Standard View */}
      {isEditing ? (
        <div className="space-y-3 pt-1">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] font-semibold text-stone-700 dark:text-stone-300">
                Proposed Instruction (Edit in place)
              </label>
              {editInstruction.length > 1000 && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.2 rounded bg-amber-50 dark:bg-amber-950 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-800"
                  title="Long directive (>1,000 chars) increases prompt token consumption, adds turn latency, and may dilute model attention across operational constraints."
                >
                  <Info className="w-2.5 h-2.5 text-amber-600 dark:text-amber-400" />
                  <span>{editInstruction.length.toLocaleString()} chars (&gt;1,000)</span>
                </span>
              )}
            </div>
            <textarea
              rows={5}
              value={editInstruction}
              onChange={(e) => setEditInstruction(e.target.value)}
              className="w-full text-xs font-mono rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-950 p-2.5 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-1 focus:ring-emerald-500 leading-relaxed"
              placeholder="Rule instruction..."
            />
          </div>

          {/* Live Diff Viewer updates in real-time as user edits! */}
          {baseline ? (
            <div className="space-y-1.5 pt-1">
              <div className="flex items-center justify-between text-[11px] font-semibold text-stone-500 dark:text-stone-400">
                <span>Live Diff Preview (updates in real time):</span>
              </div>
              <DiffViewer
                oldTitle={`Active Baseline (${baseline.title} • Priority: ${baseline.priority})`}
                newTitle={`Proposed Draft (${editTitle} • Priority: ${editPriority})`}
                oldText={baseline.instruction || ""}
                newText={editInstruction}
              />
            </div>
          ) : (
            <div className="space-y-1.5 pt-1">
              <span className="text-[11px] font-semibold text-stone-500 dark:text-stone-400">
                Draft Preview:
              </span>
              <div className="p-3 rounded-lg bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 font-mono text-xs text-stone-800 dark:text-stone-200 leading-relaxed whitespace-pre-wrap">
                {editInstruction}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Changes section dimmed when unchecked */
        <div className={`transition ${isExcluded ? "opacity-40 pointer-events-none select-none" : ""}`}>
          {baseline ? (
            <DiffViewer
              oldTitle={`Active Baseline (${baseline.title} • Priority: ${baseline.priority})`}
              newTitle={`Proposed Draft (${rule.title} • Priority: ${rule.priority})`}
              oldText={baseline.instruction || ""}
              newText={rule.instruction || ""}
            />
          ) : (
            <div className="p-3 rounded-lg bg-stone-50 dark:bg-stone-950 border border-stone-100 dark:border-stone-800 font-mono text-xs text-stone-800 dark:text-stone-200 leading-relaxed whitespace-pre-wrap">
              {rule.instruction}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface KnowledgeChangesetCardProps {
  item: any;
  isExcluded: boolean;
  isCompleted: boolean;
  onToggleExclude: (id: string) => void;
  onSave?: (itemId: string, updates: { title: string; content: string }) => Promise<void>;
  onOpenEditLegacy?: (item: any) => void;
}

function KnowledgeChangesetCard({
  item,
  isExcluded,
  isCompleted,
  onToggleExclude,
  onSave,
  onOpenEditLegacy,
}: KnowledgeChangesetCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(item.title || "");
  const [editContent, setEditContent] = useState(item.content || "");
  const [isSaving, setIsSaving] = useState(false);

  const handleStartEdit = () => {
    if (!onSave && onOpenEditLegacy) {
      onOpenEditLegacy(item);
      return;
    }
    setEditTitle(item.title || "");
    setEditContent(item.content || "");
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditTitle(item.title || "");
    setEditContent(item.content || "");
    setIsEditing(false);
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!onSave) return;
    setIsSaving(true);
    try {
      await onSave(item.id, {
        title: editTitle.trim() || item.title,
        content: editContent.trim() || item.content,
      });
      setIsEditing(false);
    } catch {
      // Error handled by parent
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={`p-4 rounded-xl border transition ${
        isEditing
          ? "bg-white dark:bg-stone-900 border-emerald-500/60 dark:border-emerald-500/50 shadow-md ring-2 ring-emerald-500/10 space-y-4"
          : isExcluded
          ? "opacity-55 grayscale-[35%] bg-stone-50/80 dark:bg-stone-950/80 border-dashed border-stone-300 dark:border-stone-800 space-y-3"
          : "bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-800 shadow-2xs space-y-3"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {isEditing ? (
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-stone-700 dark:text-stone-300">
                Document Title
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full text-xs font-bold rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50/60 dark:bg-stone-950/60 px-2.5 py-1.5 text-stone-900 dark:text-stone-100 focus:bg-white dark:focus:bg-stone-900 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                placeholder="Document title..."
              />
            </div>
          ) : (
            <div>
              <div className="flex items-center flex-wrap gap-2">
                {isCompleted ? (
                  <Link
                    to={`/knowledge/${item.id}`}
                    className="group inline-flex items-center gap-1 text-xs font-bold text-stone-900 dark:text-stone-100 hover:text-emerald-600 dark:hover:text-emerald-400 transition"
                  >
                    <span className="group-hover:underline truncate">{item.title}</span>
                    <ExternalLink className="w-3 h-3 text-stone-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 shrink-0" />
                  </Link>
                ) : (
                  <h4 className="text-xs font-bold text-stone-900 dark:text-stone-100">
                    {item.title}
                  </h4>
                )}
                <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-stone-100 dark:bg-stone-800 uppercase text-stone-600 dark:text-stone-300">
                  {item.category}
                </span>
                {isCompleted && (
                  <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                    Active Document
                  </span>
                )}
              </div>
              <span className="text-[11px] text-stone-400 block mt-0.5">
                Category: {item.category} • Source: {item.source_type || "Curation"}
              </span>
            </div>
          )}
        </div>

        {/* Right Side: Uncheck/Include toggle button moved to top right + in-place edit buttons */}
        <div className="flex items-center gap-2 shrink-0">
          {!isCompleted && (
            <button
              type="button"
              onClick={() => onToggleExclude(item.id)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition shadow-2xs ${
                isExcluded
                  ? "border border-stone-200 dark:border-stone-700 bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700 hover:text-stone-700 dark:hover:text-stone-200"
                  : "border border-emerald-200 dark:border-emerald-800/80 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/60"
              }`}
              title={isExcluded ? "Click to include in batch" : "Click to exclude from batch"}
            >
              {isExcluded ? (
                <>
                  <Square className="w-3.5 h-3.5 text-stone-400" />
                  <span>Excluded</span>
                </>
              ) : (
                <>
                  <CheckSquare className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                  <span>Included</span>
                </>
              )}
            </button>
          )}

          {isCompleted ? (
            <Link
              to={`/knowledge/${item.id}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 text-xs font-medium hover:bg-stone-50 dark:hover:bg-stone-700 hover:text-emerald-600 dark:hover:text-emerald-400 transition shadow-2xs shrink-0"
            >
              <span>Open Document</span>
              <ExternalLink className="w-3 h-3" />
            </Link>
          ) : isEditing ? (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={isSaving}
                className="px-2.5 py-1 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 text-xs hover:bg-stone-100 dark:hover:bg-stone-800 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleSave()}
                disabled={isSaving}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-2xs disabled:opacity-50 transition"
              >
                {isSaving ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <Save className="w-3 h-3" />
                )}
                <span>Save</span>
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleStartEdit}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 text-xs font-medium hover:bg-stone-50 dark:hover:bg-stone-700 hover:text-stone-900 dark:hover:text-stone-100 transition shadow-2xs shrink-0"
            >
              <Edit2 className="w-3 h-3" />
              <span>Edit</span>
            </button>
          )}
        </div>
      </div>

      {isExcluded && !isEditing && (
        <div className="px-3 py-1.5 rounded-lg bg-stone-100 dark:bg-stone-800/80 border border-stone-200 dark:border-stone-700/60 text-[11px] text-stone-500 dark:text-stone-400 flex items-center justify-between">
          <span className="font-medium">
            Document unchecked — will be excluded when promoting batch
          </span>
          <button
            type="button"
            onClick={() => onToggleExclude(item.id)}
            className="text-emerald-600 dark:text-emerald-400 hover:underline font-semibold text-[11px]"
          >
            Re-include
          </button>
        </div>
      )}

      {isEditing ? (
        <div className="space-y-1.5 pt-1">
          <label className="block text-[11px] font-semibold text-stone-700 dark:text-stone-300">
            Document Content (Markdown)
          </label>
          <textarea
            rows={8}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full text-xs font-mono rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-950 p-2.5 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-1 focus:ring-emerald-500 leading-relaxed"
            placeholder="Markdown content..."
          />
        </div>
      ) : (
        <div className={`transition ${isExcluded ? "opacity-40 pointer-events-none select-none" : ""}`}>
          <div className="p-3 rounded-lg bg-stone-50 dark:bg-stone-950 border border-stone-100 dark:border-stone-800 text-xs text-stone-800 dark:text-stone-200 leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto">
            {item.content}
          </div>
        </div>
      )}
    </div>
  );
}

interface GuardrailChangesetCardProps {
  guardrail: any;
  isExcluded: boolean;
  isCompleted: boolean;
  onToggleExclude: (id: string) => void;
  onSave?: (
    guardrailId: string,
    updates: {
      title: string;
      description?: string;
      category: string;
      action: string;
      severity: string;
      default_threshold: number;
      anchors?: Array<{ anchor_type: string; text: string }>;
    }
  ) => Promise<void>;
}

function GuardrailChangesetCard({
  guardrail,
  isExcluded,
  isCompleted,
  onToggleExclude,
  onSave,
}: GuardrailChangesetCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(guardrail.title || "");
  const [editDescription, setEditDescription] = useState(guardrail.description || "");
  const [editCategory, setEditCategory] = useState(guardrail.category || "prompt_injection");
  const [editAction, setEditAction] = useState(guardrail.action || "block");
  const [editSeverity, setEditSeverity] = useState(guardrail.severity || "high");
  const [editThreshold, setEditThreshold] = useState<number>(guardrail.default_threshold ?? 0.820);
  const [editAnchors, setEditAnchors] = useState<Array<{ anchor_type: string; text: string }>>(
    (guardrail.anchors || []).map((a: any) => ({
      anchor_type: a.anchor_type || "negative",
      text: a.text || "",
    }))
  );
  const [newAnchorText, setNewAnchorText] = useState("");
  const [newAnchorType, setNewAnchorType] = useState<"negative" | "positive">("negative");
  const [isSaving, setIsSaving] = useState(false);

  const handleStartEdit = () => {
    setEditTitle(guardrail.title || "");
    setEditDescription(guardrail.description || "");
    setEditCategory(guardrail.category || "prompt_injection");
    setEditAction(guardrail.action || "block");
    setEditSeverity(guardrail.severity || "high");
    setEditThreshold(guardrail.default_threshold ?? 0.820);
    setEditAnchors(
      (guardrail.anchors || []).map((a: any) => ({
        anchor_type: a.anchor_type || "negative",
        text: a.text || "",
      }))
    );
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditTitle(guardrail.title || "");
    setEditDescription(guardrail.description || "");
    setEditCategory(guardrail.category || "prompt_injection");
    setEditAction(guardrail.action || "block");
    setEditSeverity(guardrail.severity || "high");
    setEditThreshold(guardrail.default_threshold ?? 0.820);
    setEditAnchors(
      (guardrail.anchors || []).map((a: any) => ({
        anchor_type: a.anchor_type || "negative",
        text: a.text || "",
      }))
    );
    setIsEditing(false);
  };

  const handleAddAnchor = () => {
    if (!newAnchorText.trim()) return;
    setEditAnchors((prev) => [
      ...prev,
      { anchor_type: newAnchorType, text: newAnchorText.trim() },
    ]);
    setNewAnchorText("");
  };

  const handleRemoveAnchor = (idx: number) => {
    setEditAnchors((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!onSave) return;
    setIsSaving(true);
    try {
      await onSave(guardrail.id, {
        title: editTitle.trim() || guardrail.title,
        description: editDescription.trim(),
        category: editCategory,
        action: editAction,
        severity: editSeverity,
        default_threshold: Number(editThreshold),
        anchors: editAnchors,
      });
      setIsEditing(false);
    } catch {
      // Handled by parent
    } finally {
      setIsSaving(false);
    }
  };

  const getSeverityBadge = (sev: string) => {
    switch (sev) {
      case "critical":
        return "bg-rose-100 dark:bg-rose-950/80 text-rose-800 dark:text-rose-300 border-rose-300 dark:border-rose-800";
      case "high":
        return "bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-800";
      case "medium":
        return "bg-yellow-100 dark:bg-yellow-950/80 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-800";
      default:
        return "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border-stone-200 dark:border-stone-700";
    }
  };

  return (
    <div
      className={`p-4 rounded-xl border transition ${
        isEditing
          ? "bg-white dark:bg-stone-900 border-emerald-500/60 dark:border-emerald-500/50 shadow-md ring-2 ring-emerald-500/10 space-y-4"
          : isExcluded
          ? "opacity-55 grayscale-[35%] bg-stone-50/80 dark:bg-stone-950/80 border-dashed border-stone-300 dark:border-stone-800 space-y-3"
          : "bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-800 shadow-2xs space-y-3"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {isEditing ? (
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold text-stone-700 dark:text-stone-300 mb-1 block">
                  Guardrail Policy Title
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full text-xs font-bold rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50/60 dark:bg-stone-950/60 px-2.5 py-1.5 text-stone-900 dark:text-stone-100 focus:bg-white dark:focus:bg-stone-900 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  placeholder="Guardrail title..."
                />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <label className="text-[10px] font-semibold text-stone-500 dark:text-stone-400 block mb-0.5">
                    Category
                  </label>
                  <select
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    className="w-full text-xs rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50/60 dark:bg-stone-950/60 px-2 py-1 text-stone-800 dark:text-stone-200"
                  >
                    <option value="prompt_injection">prompt_injection</option>
                    <option value="jailbreak">jailbreak</option>
                    <option value="system_exfiltration">system_exfiltration</option>
                    <option value="off_topic">off_topic</option>
                    <option value="abusive">abusive</option>
                    <option value="competitor">competitor</option>
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-semibold text-stone-500 dark:text-stone-400 block mb-0.5">
                    Action
                  </label>
                  <select
                    value={editAction}
                    onChange={(e) => setEditAction(e.target.value)}
                    className="w-full text-xs rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50/60 dark:bg-stone-950/60 px-2 py-1 text-stone-800 dark:text-stone-200"
                  >
                    <option value="block">block (Hard Gate)</option>
                    <option value="flag">flag (Soft Advisory)</option>
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-semibold text-stone-500 dark:text-stone-400 block mb-0.5">
                    Severity
                  </label>
                  <select
                    value={editSeverity}
                    onChange={(e) => setEditSeverity(e.target.value)}
                    className="w-full text-xs rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50/60 dark:bg-stone-950/60 px-2 py-1 text-stone-800 dark:text-stone-200"
                  >
                    <option value="critical">critical</option>
                    <option value="high">high</option>
                    <option value="medium">medium</option>
                    <option value="low">low</option>
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-semibold text-stone-500 dark:text-stone-400 block mb-0.5">
                    Threshold
                  </label>
                  <input
                    type="number"
                    step="0.005"
                    min="0.5"
                    max="1.0"
                    value={editThreshold}
                    onChange={(e) => setEditThreshold(parseFloat(e.target.value))}
                    className="w-full text-xs rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50/60 dark:bg-stone-950/60 px-2 py-1 text-stone-800 dark:text-stone-200 font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-semibold text-stone-500 dark:text-stone-400 block mb-0.5">
                  Description / Threat Justification
                </label>
                <textarea
                  rows={2}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full text-xs rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50/60 dark:bg-stone-950/60 p-2 text-stone-800 dark:text-stone-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  placeholder="Explain why this guardrail is needed..."
                />
              </div>

              {/* Anchors Editor */}
              <div className="space-y-2 pt-1 border-t border-stone-100 dark:border-stone-800">
                <label className="text-[11px] font-semibold text-stone-700 dark:text-stone-300 block">
                  Anchor Phrases &amp; Reference Exemplars ({editAnchors.length})
                </label>
                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                  {editAnchors.map((anc, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 p-1.5 rounded-lg bg-stone-50 dark:bg-stone-950 border border-stone-200 dark:border-stone-800 text-xs"
                    >
                      <span
                        className={`text-[10px] font-mono px-1.5 py-0.5 rounded font-bold uppercase shrink-0 ${
                          anc.anchor_type === "negative"
                            ? "bg-rose-100 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800"
                            : "bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                        }`}
                      >
                        {anc.anchor_type}
                      </span>
                      <span className="flex-1 truncate font-mono text-[11px] text-stone-800 dark:text-stone-200">
                        "{anc.text}"
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAnchor(idx)}
                        className="text-stone-400 hover:text-rose-600 transition p-1"
                        title="Remove anchor"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add new anchor */}
                <div className="flex items-center gap-2 pt-1">
                  <select
                    value={newAnchorType}
                    onChange={(e) => setNewAnchorType(e.target.value as any)}
                    className="text-xs rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50/60 dark:bg-stone-950/60 px-2 py-1 text-stone-800 dark:text-stone-200 shrink-0 font-medium"
                  >
                    <option value="negative">Negative (Attack)</option>
                    <option value="positive">Positive (In-domain)</option>
                  </select>
                  <input
                    type="text"
                    value={newAnchorText}
                    onChange={(e) => setNewAnchorText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddAnchor();
                      }
                    }}
                    placeholder="New anchor phrase..."
                    className="flex-1 text-xs rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50/60 dark:bg-stone-950/60 px-2.5 py-1 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddAnchor}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-700 dark:text-stone-300 text-xs font-semibold shrink-0 transition"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center flex-wrap gap-2">
                <h4 className="text-xs font-bold text-stone-900 dark:text-stone-100">
                  {guardrail.title}
                </h4>

                {/* Action Badge */}
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase border tracking-wider ${
                    guardrail.action === "block"
                      ? "bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800"
                      : "bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                  }`}
                >
                  {guardrail.action === "block" ? (
                    <ShieldAlert className="w-3 h-3 text-rose-600 dark:text-rose-400 shrink-0" />
                  ) : (
                    <Flag className="w-3 h-3 text-amber-600 dark:text-amber-400 shrink-0" />
                  )}
                  <span>{guardrail.action}</span>
                </span>

                {/* Category Pill */}
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-800 uppercase text-stone-600 dark:text-stone-300 border border-stone-200 dark:border-stone-700">
                  {guardrail.category}
                </span>

                {/* Severity */}
                <span
                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase border ${getSeverityBadge(
                    guardrail.severity
                  )}`}
                >
                  {guardrail.severity}
                </span>

                {/* Threshold */}
                <span className="text-[10px] font-mono text-stone-400 bg-stone-50 dark:bg-stone-950 px-1.5 py-0.5 rounded border border-stone-200 dark:border-stone-800">
                  threshold: {Number(guardrail.default_threshold ?? 0.820).toFixed(3)}
                </span>

                {isCompleted && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                    Active Guardrail
                  </span>
                )}
              </div>

              {guardrail.description && (
                <p className="text-xs text-stone-600 dark:text-stone-400 mt-1 leading-relaxed">
                  {guardrail.description}
                </p>
              )}

              <span className="text-[11px] text-stone-400 block mt-1">
                Scope: {guardrail.agent_type ? `Agent: ${normalizeAgentType(guardrail.agent_type)}` : "Global"} • Status: {guardrail.status}
              </span>
            </div>
          )}
        </div>

        {/* Right Side Buttons */}
        <div className="flex items-center gap-2 shrink-0">
          {!isCompleted && (
            <button
              type="button"
              onClick={() => onToggleExclude(guardrail.id)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition shadow-2xs ${
                isExcluded
                  ? "border border-stone-200 dark:border-stone-700 bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700 hover:text-stone-700 dark:hover:text-stone-200"
                  : "border border-emerald-200 dark:border-emerald-800/80 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/60"
              }`}
              title={isExcluded ? "Click to include in batch" : "Click to exclude from batch"}
            >
              {isExcluded ? (
                <>
                  <Square className="w-3.5 h-3.5 text-stone-400" />
                  <span>Excluded</span>
                </>
              ) : (
                <>
                  <CheckSquare className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                  <span>Included</span>
                </>
              )}
            </button>
          )}

          {!isCompleted &&
            (isEditing ? (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="px-2.5 py-1 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 text-xs hover:bg-stone-100 dark:hover:bg-stone-800 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleSave()}
                  disabled={isSaving}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-2xs disabled:opacity-50 transition"
                >
                  {isSaving ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : (
                    <Save className="w-3 h-3" />
                  )}
                  <span>Save</span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleStartEdit}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 text-xs font-medium hover:bg-stone-50 dark:hover:bg-stone-700 hover:text-stone-900 dark:hover:text-stone-100 transition shadow-2xs shrink-0"
              >
                <Edit2 className="w-3 h-3" />
                <span>Edit</span>
              </button>
            ))}
        </div>
      </div>

      {isExcluded && !isEditing && (
        <div className="px-3 py-1.5 rounded-lg bg-stone-100 dark:bg-stone-800/80 border border-stone-200 dark:border-stone-700/60 text-[11px] text-stone-500 dark:text-stone-400 flex items-center justify-between">
          <span className="font-medium">
            Guardrail policy unchecked — will be excluded when promoting batch
          </span>
          <button
            type="button"
            onClick={() => onToggleExclude(guardrail.id)}
            className="text-emerald-600 dark:text-emerald-400 hover:underline font-semibold text-[11px]"
          >
            Re-include
          </button>
        </div>
      )}

      {/* Non-editing Anchors Display */}
      {!isEditing && (
        <div className={`space-y-2 pt-1 ${isExcluded ? "opacity-40 pointer-events-none select-none" : ""}`}>
          <div className="flex items-center justify-between text-[11px] text-stone-500 dark:text-stone-400">
            <span className="font-semibold flex items-center gap-1">
              <Shield className="w-3.5 h-3.5 text-stone-400" />
              <span>Embedding Anchors ({guardrail.anchors?.length || guardrail.anchor_count || 0})</span>
            </span>
            <span className="text-[10px] font-mono text-stone-400">Dual Manifold (all-MiniLM-L6-v2)</span>
          </div>

          <div className="p-3 rounded-lg bg-stone-50 dark:bg-stone-950 border border-stone-200/80 dark:border-stone-800/80 space-y-2">
            {(!guardrail.anchors || guardrail.anchors.length === 0) ? (
              <div className="text-stone-400 text-[11px] italic">No reference anchors defined.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {guardrail.anchors.map((a: any, i: number) => {
                  const isNeg = a.anchor_type === "negative";
                  return (
                    <div
                      key={a.id || i}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-mono border ${
                        isNeg
                          ? "bg-rose-50/70 dark:bg-rose-950/40 border-rose-200 dark:border-rose-900/60 text-rose-800 dark:text-rose-300"
                          : "bg-emerald-50/70 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900/60 text-emerald-800 dark:text-emerald-300"
                      }`}
                    >
                      {isNeg ? (
                        <ShieldAlert className="w-3 h-3 text-rose-500 shrink-0" />
                      ) : (
                        <ShieldCheck className="w-3 h-3 text-emerald-500 shrink-0" />
                      )}
                      <span className="truncate max-w-sm">"{a.text}"</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface BatchChangesetTabProps {
  subtab: "rules" | "knowledge" | "guardrails";
  onChangeSubtab: (subtab: "rules" | "knowledge" | "guardrails") => void;
  draftRules: any[];
  draftKnowledgeItems: any[];
  draftGuardrails?: any[];
  excludedRuleIds: Set<string>;
  excludedKnowledgeIds: Set<string>;
  excludedGuardrailIds?: Set<string>;
  onToggleExcludeRule: (id: string) => void;
  onToggleExcludeKnowledge: (id: string) => void;
  onToggleExcludeGuardrail?: (id: string) => void;
  onSaveRule?: (ruleId: string, updates: { title: string; instruction: string; priority: number }) => Promise<void>;
  onSaveKnowledge?: (itemId: string, updates: { title: string; content: string }) => Promise<void>;
  onSaveGuardrail?: (
    guardrailId: string,
    updates: {
      title: string;
      description?: string;
      category: string;
      action: string;
      severity: string;
      default_threshold: number;
      anchors?: Array<{ anchor_type: string; text: string }>;
    }
  ) => Promise<void>;
  onOpenEditRule?: (rule: any) => void;
  onOpenEditKnowledge?: (item: any) => void;
  baselineRules: Map<string, RuleResponse>;
  isCompleted?: boolean;
}

export function BatchChangesetTab({
  subtab,
  onChangeSubtab,
  draftRules,
  draftKnowledgeItems,
  draftGuardrails = [],
  excludedRuleIds,
  excludedKnowledgeIds,
  excludedGuardrailIds = new Set(),
  onToggleExcludeRule,
  onToggleExcludeKnowledge,
  onToggleExcludeGuardrail = () => {},
  onSaveRule,
  onSaveKnowledge,
  onSaveGuardrail,
  onOpenEditRule,
  onOpenEditKnowledge,
  baselineRules,
  isCompleted = false,
}: BatchChangesetTabProps) {
  return (
    <div className="space-y-5">
      {/* Changeset Subtabs Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 bg-stone-100 dark:bg-stone-800 p-1 rounded-lg text-xs font-medium">
          <button
            type="button"
            onClick={() => onChangeSubtab("rules")}
            className={`px-3 py-1 rounded-md transition ${
              subtab === "rules"
                ? "bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 font-bold shadow-2xs"
                : "text-stone-500 hover:text-stone-800 dark:hover:text-stone-200"
            }`}
          >
            Rules ({draftRules.length})
          </button>
          <button
            type="button"
            onClick={() => onChangeSubtab("knowledge")}
            className={`px-3 py-1 rounded-md transition ${
              subtab === "knowledge"
                ? "bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 font-bold shadow-2xs"
                : "text-stone-500 hover:text-stone-800 dark:hover:text-stone-200"
            }`}
          >
            Knowledge Items ({draftKnowledgeItems.length})
          </button>
          <button
            type="button"
            onClick={() => onChangeSubtab("guardrails")}
            className={`px-3 py-1 rounded-md transition ${
              subtab === "guardrails"
                ? "bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 font-bold shadow-2xs"
                : "text-stone-500 hover:text-stone-800 dark:hover:text-stone-200"
            }`}
          >
            Guardrails ({draftGuardrails.length})
          </button>
        </div>

        {isCompleted ? (
          <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
            <span>Batch promoted • Items active in catalog</span>
          </div>
        ) : (
          <div className="text-xs text-stone-400">
            Selective approval: uncheck items in top-right to exclude from promotion
          </div>
        )}
      </div>

      {subtab === "rules" ? (
        draftRules.length === 0 ? (
          <div className="p-8 text-center text-xs text-stone-400 bg-stone-50 dark:bg-stone-950/50 rounded-xl border border-stone-200 dark:border-stone-800">
            {isCompleted
              ? "No rules were created as part of this batch."
              : 'No proposed rule changes in this batch. Click "Process Batch" to synthesize proposals.'}
          </div>
        ) : (
          <div className="space-y-4">
            {draftRules.map((rule) => {
              const isExcluded = excludedRuleIds.has(rule.id);
              const baseline = rule.supersedes_rule_id
                ? baselineRules.get(rule.supersedes_rule_id)
                : null;

              return (
                <RuleChangesetCard
                  key={rule.id}
                  rule={rule}
                  baseline={baseline}
                  isExcluded={isExcluded}
                  isCompleted={isCompleted}
                  onToggleExclude={onToggleExcludeRule}
                  onSave={onSaveRule}
                  onOpenEditLegacy={onOpenEditRule}
                />
              );
            })}
          </div>
        )
      ) : subtab === "knowledge" ? (
        draftKnowledgeItems.length === 0 ? (
          <div className="p-8 text-center text-xs text-stone-400 bg-stone-50 dark:bg-stone-950/50 rounded-xl border border-stone-200 dark:border-stone-800">
            {isCompleted
              ? "No knowledge items were created as part of this batch."
              : "No proposed knowledge articles in this batch."}
          </div>
        ) : (
          <div className="space-y-4">
            {draftKnowledgeItems.map((item) => {
              const isExcluded = excludedKnowledgeIds.has(item.id);
              return (
                <KnowledgeChangesetCard
                  key={item.id}
                  item={item}
                  isExcluded={isExcluded}
                  isCompleted={isCompleted}
                  onToggleExclude={onToggleExcludeKnowledge}
                  onSave={onSaveKnowledge}
                  onOpenEditLegacy={onOpenEditKnowledge}
                />
              );
            })}
          </div>
        )
      ) : draftGuardrails.length === 0 ? (
        <div className="p-8 text-center text-xs text-stone-400 bg-stone-50 dark:bg-stone-950/50 rounded-xl border border-stone-200 dark:border-stone-800">
          {isCompleted
            ? "No guardrail policies were created as part of this batch."
            : "No proposed guardrail policies in this batch."}
        </div>
      ) : (
        <div className="space-y-4">
          {draftGuardrails.map((guardrail) => {
            const isExcluded = excludedGuardrailIds.has(guardrail.id);
            return (
              <GuardrailChangesetCard
                key={guardrail.id}
                guardrail={guardrail}
                isExcluded={isExcluded}
                isCompleted={isCompleted}
                onToggleExclude={onToggleExcludeGuardrail}
                onSave={onSaveGuardrail}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
